import React, {useEffect, useRef, useState} from "react";
import {PropertyDto} from "../../../../../common/apis/property/PropertyApi";
import {AttributeDefinitionDto} from "../../../../../common/apis/stereotype/AttributeDefinitionDto";
import {CancelButton} from "../../../../../components/button/CancelButton";
import {SaveButton} from "../../../../../components/button/SaveButton";
import Dialog from "../../../../../components/dialogs/Dialog";
import DialogActions from "../../../../../components/dialogs/DialogActions";
import DialogContent from "../../../../../components/dialogs/DialogContent";
import DialogTitle from "../../../../../components/dialogs/DialogTitle";
import Grid from "../../../../../components/dialogs/Grid";
import TextField from "../../../../../components/fields/textfield/TextField";
import {t} from "../../../../../store/localization/TranslMessasge";
import {CommonTranslation} from "../../CommonTranslation";
import {CoreDataTypeFormatter} from "../../datatype/CoreDataTypeFormatter";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import Snackbar from "../../snackbar/Snackbar";
import {AttributeValueValidator} from "../../stereotypes/attributes/AttributeValueValidator";
import {PropertiesTranslationKey} from "../PropertiesTranslationKey";
import {PropertyDefinitionIdConverter} from "../PropertyDefinitionIdConverter";
import {PropertyValidator} from "../PropertyValidator";


export interface PropertyUpdateApi {
    updateProperty: (propertyDefinitionId: string, value: string) => Promise<void>;
    findAllAttributeDefinitions: () => Promise<AttributeDefinitionDto[]>;
}

interface PropertyUpdateDialogProps {
    open: boolean;
    property: PropertyDto;
    propertyUpdateApi: PropertyUpdateApi;
    onUpdated: () => void;
    onClosed: () => void;
}

export default function PropertyUpdateDialog(props: Readonly<PropertyUpdateDialogProps>) {
    const {open, property, propertyUpdateApi, onUpdated, onClosed} = props
    const [value, setValue] = useState<string>(property.value);
    const [valueValidationMsg, setValueValidationMsg] = useState<string | undefined>(undefined);
    const [attributeDefinitions, setAttributeDefinitions] = useState<AttributeDefinitionDto[]>([]);

    const validator = useRef(new PropertyValidator());
    const valueValidator = useRef(new AttributeValueValidator());

    useEffect(() => {
        setValue(property.value);
        setValueValidationMsg(undefined);
    }, [property]);

    useEffect(() => {
        propertyUpdateApi.findAllAttributeDefinitions()
                .then(attributeDefinitions => setAttributeDefinitions(attributeDefinitions));
    }, [propertyUpdateApi]);

    function validateValue(text: string): boolean {
        const attributeDefinition = findAttributeDefinition(property.definition.identifier);
        if (attributeDefinition !== undefined) {
            const errors = valueValidator.current.validateValue(text, attributeDefinition, t);
            if (errors[attributeDefinition.id]) {
                setValueValidationMsg(errors[attributeDefinition.id]);
                return false;
            } else {
                return true;
            }
        } else {
            const isValid = validator.current.validateValue(text);
            setValueValidationMsg(isValid ? undefined : t(CommonTranslation.FILL_OUT_THIS_FIELD));
            return isValid;
        }
    }

    function findAttributeDefinition(propertyDefinitionIdentifier: string | undefined): AttributeDefinitionDto | undefined {
        if (propertyDefinitionIdentifier === undefined) {
            return undefined;
        }
        const attributeDefId = PropertyDefinitionIdConverter.convertToAttrDefId(propertyDefinitionIdentifier);
        return attributeDefinitions.find(attributeDefinition => attributeDefinition.id === attributeDefId);
    }

    function updateProperty() {
        const isValueValid = validateValue(value);
        if (isValueValid) {
            propertyUpdateApi.updateProperty(property.definition.identifier, value)
                .then(() => {
                    onUpdated();
                    onClosed();
                    Snackbar.success(t(PropertiesTranslationKey.PROPERTY_UPDATED_SUCCESSFULLY));
                })
                .catch((error) => Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error));
        }
    }

    return (
        <Dialog open={open} onClose={onClosed}>
            <DialogTitle id="scroll-dialog-title"
                         title={t(PropertiesTranslationKey.UPDATE_PROPERTY)}
                         onDialogClosed={onClosed}/>
            <DialogContent>
                <Grid container>
                    <Grid item xs={12}>
                        <TextField id="name-field"
                                   label={t(PropertiesTranslationKey.NAME)}
                                   value={property.definition.name}
                                   disabled={true}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField id="type-field"
                                   label={t(PropertiesTranslationKey.TYPE)}
                                   value={CoreDataTypeFormatter.formatCoreDataType(property.definition.type, t)}
                                   disabled={true}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField id="value-field"
                                   label={t(PropertiesTranslationKey.VALUE)}
                                   value={value}
                                   required={true}
                                   errorMessage={valueValidationMsg}
                                   onChange={(newValue) => setValue(newValue)}/>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <SaveButton onClick={updateProperty}/>
                <CancelButton onClick={onClosed} variant={"outlined"}/>
            </DialogActions>
        </Dialog>
    );
}
